import type { PropsWithChildren } from 'react';
import React, { createContext, useMemo, useCallback } from 'react';
import moment from 'moment';
import { useAtom } from 'jotai/react';
import { persistedAtom } from '../../state/utils/persistedAtom';
import {
  TIMELINE_WINDOW_KEY,
  timelineWindowSchema,
} from '../../state/planning/session';

export const DEFAULT_WEEKS_BEFORE_TODAY = 1;
export const DEFAULT_WEEKS_AFTER_TODAY = 6;
export const DEFAULT_ZOOM_WEEKS =
  DEFAULT_WEEKS_BEFORE_TODAY + DEFAULT_WEEKS_AFTER_TODAY;
const MILLISECONDS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

export interface TimelinePreferencesContextProps {
  currentZoomLevel: number;
  setVisibleTime: (start: number, end: number) => void;
  setZoomLevel: (weeks: number) => void;
  stepScroll: (forward: boolean) => void;
  visibleTimeEnd: number;
  visibleTimeStart: number;
}

const initialCenter = moment().valueOf();
const initialStart = moment(initialCenter)
  .subtract(DEFAULT_WEEKS_BEFORE_TODAY, 'weeks')
  .valueOf();
const initialEnd = moment(initialCenter)
  .add(DEFAULT_WEEKS_AFTER_TODAY, 'weeks')
  .valueOf();

// Module-level singleton so both TimelinePreferencesProvider mounts (app-wide in
// _app.tsx and the planning-nested one) share and persist the same visible window
// (SEABER-6666). start = scroll position, (end - start) = zoom span.
const timelineWindowAtom = persistedAtom({
  key: TIMELINE_WINDOW_KEY,
  schema: timelineWindowSchema,
  defaultValue: { start: initialStart, end: initialEnd },
  /** Timeline must render at the restored window on first paint. */
  getOnInit: true,
});

export const TimelinePreferencesContext =
  createContext<TimelinePreferencesContextProps>({
    currentZoomLevel: DEFAULT_ZOOM_WEEKS,
    setVisibleTime: () => void null,
    setZoomLevel: () => void null,
    stepScroll: () => void null,
    visibleTimeEnd: initialEnd,
    visibleTimeStart: initialStart,
  });

/** Preserves common state for all timeline components so that we can keep the same zoom and location
 * when switching between preview and planning timelines */
export const TimelinePreferencesProvider = ({
  children,
}: PropsWithChildren<unknown>): JSX.Element => {
  const [{ start: visibleTimeStart, end: visibleTimeEnd }, setTimelineWindow] =
    useAtom(timelineWindowAtom);
  const currentZoomLevel =
    (visibleTimeEnd - visibleTimeStart) / MILLISECONDS_PER_WEEK;

  const setVisibleTime = useCallback(
    (start: number, end: number) => {
      setTimelineWindow({ start, end });
    },
    [setTimelineWindow],
  );

  const setZoomLevel = useCallback(
    (weeks: number) => {
      const center = visibleTimeStart + (visibleTimeEnd - visibleTimeStart) / 2;
      const millisecondsFromCenter = (weeks * MILLISECONDS_PER_WEEK) / 2;

      const newStart = center - millisecondsFromCenter;
      const newEnd = center + millisecondsFromCenter;

      setVisibleTime(newStart, newEnd);
    },
    [visibleTimeStart, visibleTimeEnd, setVisibleTime],
  );

  const stepScroll = useCallback(
    (forward: boolean) => {
      const timeRange = visibleTimeEnd - visibleTimeStart;
      const stepSize = timeRange * 0.3; // Step by 30% of the visible range
      const newStart = visibleTimeStart + (forward ? stepSize : -stepSize);
      const newEnd = visibleTimeEnd + (forward ? stepSize : -stepSize);
      setVisibleTime(newStart, newEnd);
    },
    [visibleTimeStart, visibleTimeEnd, setVisibleTime],
  );

  const contextValue = useMemo(
    () => ({
      currentZoomLevel,
      setVisibleTime,
      setZoomLevel,
      stepScroll,
      visibleTimeEnd,
      visibleTimeStart,
    }),
    [
      currentZoomLevel,
      setVisibleTime,
      setZoomLevel,
      stepScroll,
      visibleTimeEnd,
      visibleTimeStart,
    ],
  );

  return (
    <TimelinePreferencesContext.Provider value={contextValue}>
      {children}
    </TimelinePreferencesContext.Provider>
  );
};

export default TimelinePreferencesContext;
